import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@material-ui/core";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import TableGrid from "../../waybee-ui/TableGrid";
import UserStore from "../../utils/UserStore";
import {
  buildAdvancedSections,
  resolveViewerTipo,
  visibleNiveisForViewer
} from "../../utils/occurrenceAdvanced";
import OccurrenceIcon from "./OccurrenceIcon";
import OccurrenceStackCard from "./OccurrenceStackCard";
import { formatOccurrenceDate } from "./occurrenceDisplay";

const SectionTitle = styled(Typography)`
  font-weight: 600;
  margin: 8px 0 12px;
`;

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin: 0 0 16px;
`;

function OccurrenceHistoryRow({ occur, primaryColor, dateLocale, onSelect }) {
  const { t } = useTranslation();
  const stageLabel = String(occur.stageShortName || "").trim();
  return (
    <TableGrid.Row
      actionIcon
      button={!!onSelect}
      onClick={onSelect ? () => onSelect(occur) : undefined}
    >
      <TableGrid.Cell xs={1}>
        <OccurrenceIcon
          occurrence={occur}
          fallbackColor={primaryColor}
          size={36}
        />
      </TableGrid.Cell>
      <TableGrid.Title xs={occur.studentName ? 4 : 6}>
        {occur.title || occur.reason}
      </TableGrid.Title>
      {occur.studentName ? (
        <TableGrid.Text xs={2}>{occur.studentName}</TableGrid.Text>
      ) : null}
      <TableGrid.Text xs={1}>{stageLabel || "—"}</TableGrid.Text>
      <TableGrid.Text xs={3}>
        {formatOccurrenceDate(occur, dateLocale, t)}
      </TableGrid.Text>
    </TableGrid.Row>
  );
}

OccurrenceHistoryRow.propTypes = {
  occur: PropTypes.object.isRequired,
  primaryColor: PropTypes.string.isRequired,
  dateLocale: PropTypes.string.isRequired,
  onSelect: PropTypes.func
};

OccurrenceHistoryRow.defaultProps = {
  onSelect: undefined
};

export default function AdvancedOccurrencesLayout({
  occurrences,
  config,
  primaryColor,
  dateLocale,
  onSelect,
  emptyMessage
}) {
  const { t } = useTranslation();
  const viewerTipo = resolveViewerTipo(UserStore.getUserExternalInfo(), config);
  const visibleNiveis = visibleNiveisForViewer(viewerTipo);
  const sections = buildAdvancedSections(occurrences, config, {
    primaryColor,
    visibleNiveis
  });

  const isEmpty =
    !sections.level3.length && !sections.level2.length && !sections.list.length;

  if (isEmpty) {
    return <Typography>{emptyMessage}</Typography>;
  }

  return (
    <Box>
      {sections.level3.length > 0 && (
        <>
          <SectionTitle variant="subtitle1">
            {t("occurrences.advanced.level3")}
            {` (${sections.level3.reduce(
              (sum, stack) => sum + stack.count,
              0
            )})`}
          </SectionTitle>
          <CardsRow>
            {sections.level3.map(stack => (
              <OccurrenceStackCard
                key={`l3-${stack.key}`}
                stack={stack}
                primaryColor={primaryColor}
                dateLocale={dateLocale}
                onSelect={onSelect}
              />
            ))}
          </CardsRow>
        </>
      )}
      {sections.level2.length > 0 && (
        <>
          <SectionTitle variant="subtitle1">
            {t("occurrences.advanced.level2")}
            {` (${sections.level2.reduce(
              (sum, stack) => sum + stack.count,
              0
            )})`}
          </SectionTitle>
          <CardsRow>
            {sections.level2.map(stack => (
              <OccurrenceStackCard
                key={`l2-${stack.key}`}
                stack={stack}
                primaryColor={primaryColor}
                dateLocale={dateLocale}
                onSelect={onSelect}
              />
            ))}
          </CardsRow>
        </>
      )}
      {sections.list.length > 0 && (
        <>
          <SectionTitle variant="subtitle1">
            {t("occurrences.advanced.level1")}
            {` (${sections.list.length})`}
          </SectionTitle>
          <TableGrid>
            {sections.list.map(occur => (
              <OccurrenceHistoryRow
                key={occur.id || `${occur.date}-${occur.reason}`}
                occur={occur}
                primaryColor={primaryColor}
                dateLocale={dateLocale}
                onSelect={onSelect}
              />
            ))}
          </TableGrid>
        </>
      )}
    </Box>
  );
}

AdvancedOccurrencesLayout.propTypes = {
  occurrences: PropTypes.arrayOf(PropTypes.object),
  config: PropTypes.object,
  primaryColor: PropTypes.string.isRequired,
  dateLocale: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  emptyMessage: PropTypes.string
};

AdvancedOccurrencesLayout.defaultProps = {
  occurrences: [],
  config: undefined,
  emptyMessage: "",
  onSelect: undefined
};
